import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Drawer,
  Form,
  Input,
  Switch,
  Button,
  Space,
  Checkbox,
  message,
  Typography,
  Divider,
  AutoComplete,
  Select,
  Tag
} from "antd";

import { CopyOutlined, DeleteOutlined, LinkOutlined, PlusOutlined } from "@ant-design/icons";

import {
  createBrandJourneyConfigurationAPI,
  updateBrandJourneyConfigurationAPI,
  searchRxnormDrugsAPI
} from "../../api/api";
import { ImageUploadField } from "../../component/ImageUploadField";

const { Text } = Typography;

const formatPhoneNumber = (value) => {
  const digits = (value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const DEFAULT_JOURNEY_STEPS = {
  prescription: {
    enabled: true,
    subSteps: {
      prescriberInfo: true,
      pharmacyInfusionCenter: true,
      refillReminders: true
    }
  },
  coverage: {
    enabled: true,
    subSteps: {
      insuranceDetails: true,
      priorAuthorization: true
    }
  },
  assistance: {
    enabled: true,
    subSteps: {
      assistancePrograms: true,
      copayWallet: true
    }
  },
  support: {
    enabled: true,
    subSteps: {
      supportPrograms: true,
      communities: true
    }
  }
};

const SUB_STEP_LABELS = {
  prescriberInfo: "Prescriber Info",
  pharmacyInfusionCenter: "Pharmacy / Infusion Center",
  refillReminders: "Refill Reminders",
  insuranceDetails: "Insurance Details",
  priorAuthorization: "Prior Authorization",
  assistancePrograms: "Assistance Programs",
  copayWallet: "Copay Wallet",
  supportPrograms: "Support Programs & Hub",
  communities: "Communities"
};

const STEP_LABELS = {
  prescription: "Prescription",
  coverage: "Coverage",
  assistance: "Assistance",
  support: "Support"
};

const EMPTY_RESOURCE = { type: "support_program", title: "", description: "", linkUrl: "", logoUrl: "" };

const JourneyConfigurationForm = ({ visible, config, onClose }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [journeySteps, setJourneySteps] = useState(config?.journeySteps || DEFAULT_JOURNEY_STEPS);
  const [supportResources, setSupportResources] = useState(config?.supportResources || []);
  const [drugSearchResults, setDrugSearchResults] = useState([]);
  // Local blob URLs for image preview — avoids S3 private-bucket auth issues
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState(null);
  const debounceRef = useRef(null);

  const isEditing = !!config;

  // Reset local preview URLs whenever the form opens (new brand or different brand)
  useEffect(() => {
    setLogoPreviewUrl(null);
    setBannerPreviewUrl(null);
  }, [visible, config?.id]);

  useEffect(() => {
    if (config) {
      form.setFieldsValue({
        brandName: config.brandName,
        brandLogoUrl: config.brandLogoUrl,
        bannerImageUrl: config.bannerImageUrl,
        manufacturerHotlineNumber: config.manufacturerHotlineNumber,
        aiSupportPhoneNumber: config.aiSupportPhoneNumber,
        rxCui: config.rxCui,
        rxnormDrugName: config.rxnormDrugName
      });
      setJourneySteps(config.journeySteps || DEFAULT_JOURNEY_STEPS);
      setSupportResources(config.supportResources || []);
    } else {
      form.resetFields();
      setJourneySteps(DEFAULT_JOURNEY_STEPS);
      setSupportResources([]);
    }
  }, [config, form]);

  const fetchDrugs = useCallback(async (searchText) => {
    try {
      const response = await searchRxnormDrugsAPI({ text: searchText });
      const drugs = response?.data?.rxnormDrugs || [];
      const results = drugs.map((drug) => ({
        value: String(
          drug.bnName ? `${drug.bnName} (${drug.pinName || ""})` : drug.pinName || drug.tmDisplayName || ""
        ),
        label: drug.bnName ? `${drug.bnName} (${drug.pinName || ""})` : drug.pinName || drug.tmDisplayName || "",
        key: String(drug.id),
        drug
      }));
      setDrugSearchResults(results);
    } catch (err) {
      console.error("Drug search error:", err);
      setDrugSearchResults([]);
    }
  }, []);

  const handleDrugSearch = (searchText) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchText || searchText.length < 2) {
      setDrugSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => fetchDrugs(searchText), 350);
  };

  const handleDrugSelect = (value, option) => {
    const drug = option.drug;
    const brandDisplayName = drug.bnName || drug.tmDisplayName || value;
    const fullDrugName = drug.bnName ? `${drug.bnName} (${drug.pinName || ""})` : drug.pinName || value;
    form.setFieldsValue({
      brandName: brandDisplayName,
      rxCui: String(drug.id || ""),
      rxnormDrugName: fullDrugName
    });
  };

  const handleStepToggle = (stepKey, enabled) => {
    setJourneySteps((prev) => ({
      ...prev,
      [stepKey]: { ...prev[stepKey], enabled }
    }));
  };

  const handleSubStepToggle = (stepKey, subStepKey, value) => {
    setJourneySteps((prev) => ({
      ...prev,
      [stepKey]: {
        ...prev[stepKey],
        subSteps: {
          ...prev[stepKey].subSteps,
          [subStepKey]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const data = {
        ...values,
        journeySteps,
        supportResources
      };

      if (isEditing) {
        await updateBrandJourneyConfigurationAPI(config.id, data);
        message.success(`Brand "${values.brandName}" updated successfully`);
      } else {
        await createBrandJourneyConfigurationAPI(data);
        message.success(`Brand "${values.brandName}" created successfully`);
      }

      onClose(true);
    } catch (error) {
      if (error.response?.data) {
        message.error(error.response.data);
      } else if (!error.errorFields) {
        message.error("Failed to save brand configuration");
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Support Resources helpers ──────────────────────────────────────────────

  const addResource = () => {
    setSupportResources((prev) => [...prev, { ...EMPTY_RESOURCE }]);
  };

  const removeResource = (index) => {
    setSupportResources((prev) => prev.filter((_, i) => i !== index));
  };

  const updateResource = (index, field, value) => {
    setSupportResources((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const renderSupportResources = () => (
    <div style={{ marginTop: 16 }}>
      <Divider style={{ margin: "12px 0" }} />
      <Space style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 13 }}>
          Support Resources
        </Text>
        <Tag color="blue">{supportResources.filter((r) => r.type === "support_program").length} programs</Tag>
        <Tag color="purple">{supportResources.filter((r) => r.type === "community").length} communities</Tag>
      </Space>
      <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
        Define the programs and communities shown to patients in the Support step. These appear as clickable cards
        linking to an external URL.
      </Text>

      {supportResources.map((resource, index) => (
        <div
          key={index}
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: "10px 12px",
            marginBottom: 8,
            background: resource.type === "community" ? "#f9f0ff" : "#f0f7ff"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Select
              value={resource.type}
              onChange={(value) => updateResource(index, "type", value)}
              size="small"
              style={{ width: 160 }}
            >
              <Select.Option value="support_program">Support Program</Select.Option>
              <Select.Option value="community">Community</Select.Option>
            </Select>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => removeResource(index)} />
          </div>
          <Input
            placeholder="Title (e.g., Better Breathers Club)"
            value={resource.title}
            onChange={(e) => updateResource(index, "title", e.target.value)}
            size="small"
            style={{ marginBottom: 6 }}
          />
          <Input
            placeholder="Description (1-2 sentences)"
            value={resource.description}
            onChange={(e) => updateResource(index, "description", e.target.value)}
            size="small"
            style={{ marginBottom: 6 }}
          />
          <Input
            prefix="🔗"
            placeholder="Link URL (https://...)"
            value={resource.linkUrl}
            onChange={(e) => updateResource(index, "linkUrl", e.target.value)}
            size="small"
            style={{ marginBottom: 6 }}
          />
          <Input
            prefix="🖼️"
            placeholder="Logo / Image URL (optional)"
            value={resource.logoUrl}
            onChange={(e) => updateResource(index, "logoUrl", e.target.value)}
            size="small"
          />
        </div>
      ))}

      <Button type="dashed" block icon={<PlusOutlined />} onClick={addResource} style={{ marginTop: 4 }}>
        Add Resource
      </Button>
    </div>
  );

  // ──────────────────────────────────────────────────────────────────────────

  // ─── Live Preview Component (phone mockup) ─────────────────────────────────
  const LivePreview = () => (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldValue }) => {
        const logoUrl = logoPreviewUrl || getFieldValue("brandLogoUrl");
        const bName = getFieldValue("brandName");
        const drugDisplay = getFieldValue("rxnormDrugName");
        const enabledStepLabels = Object.entries(journeySteps)
          .filter(([, v]) => v?.enabled)
          .map(([k]) => STEP_LABELS[k]);

        return (
          <div style={{ position: "sticky", top: 20 }}>
            <div style={{ color: "#0C85A3", fontWeight: 700, fontSize: 13, marginBottom: 4 }}>LIVE PREVIEW</div>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
              Live Preview of how this brand will look in TMConnect
            </Text>
            {/* Phone frame */}
            <div
              style={{
                background: "#f0f2f5",
                borderRadius: 36,
                padding: "14px 8px 10px",
                maxWidth: 290,
                boxShadow: "0 2px 20px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
                border: "1px solid #e0e0e0"
              }}
            >
              <div
                style={{
                  borderRadius: 24,
                  overflow: "hidden",
                  background: "#F6F2ED",
                  height: 560,
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)"
                }}
              >
                {/* Header */}
                <div
                  style={{
                    background: "#fff",
                    padding: "10px 12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #f0f0f0",
                    flexShrink: 0
                  }}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt=""
                      style={{ height: 20, maxWidth: 90, objectFit: "contain" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <Text strong style={{ fontSize: 11, color: "#0C85A3" }}>
                      {bName || "Brand"}
                    </Text>
                  )}
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "#EBF9FD",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 9
                    }}
                  >
                    👤
                  </div>
                </div>

                {/* Scrollable content */}
                <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px" }}>
                  <Text style={{ fontSize: 9, color: "#8F8C86" }}>Hello Matthew,</Text>
                  <Text
                    strong
                    style={{ fontSize: 13, display: "block", color: "#393735", lineHeight: 1.2, marginBottom: 2 }}
                  >
                    Start your medication journey
                  </Text>
                  <Text style={{ fontSize: 8, color: "#8F8C86", display: "block", marginBottom: 10 }}>
                    Choose a medication to see available assistance options
                  </Text>

                  {/* Drug card for this brand */}
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      padding: "8px 10px",
                      border: "1px solid #EAE5DD",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                      marginBottom: 6
                    }}
                  >
                    <div style={{ display: "flex", gap: 4, marginBottom: 3 }}>
                      <span
                        style={{
                          fontSize: 8,
                          background: "#EBF9FD",
                          border: "1px solid #CDE6EC",
                          borderRadius: 20,
                          padding: "0px 5px",
                          color: "#389BB4"
                        }}
                      >
                        New
                      </span>
                      <span style={{ marginLeft: "auto", fontSize: 9, color: "#bfbfbf" }}>›</span>
                    </div>
                    <Text strong style={{ fontSize: 11, color: "#393735", display: "block" }}>
                      {bName || "Medication"}
                    </Text>
                    {drugDisplay && <Text style={{ fontSize: 8, color: "#8F8C86" }}>({drugDisplay})</Text>}
                    <div
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}
                    >
                      <Text style={{ fontSize: 8, color: "#0C85A3", fontWeight: 700 }}>
                        {enabledStepLabels[0] || "Prescription"}
                      </Text>
                      <Text style={{ fontSize: 8, color: "#8F8C86" }}>{enabledStepLabels.length * 2} open tasks</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 1, marginTop: 3 }}>
                      {enabledStepLabels.map((_, si) => (
                        <React.Fragment key={si}>
                          <div
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: si === 0 ? "#0C85A3" : "#EBF9FD"
                            }}
                          />
                          {si < enabledStepLabels.length - 1 && (
                            <div style={{ flex: 1, height: 2.5, background: si === 0 ? "#0C85A3" : "#EBF9FD" }} />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>

                  {/* Add Medication */}
                  <div
                    style={{
                      border: "1.5px dashed #CDE6EC",
                      borderRadius: 12,
                      padding: "8px",
                      textAlign: "center",
                      marginTop: 2
                    }}
                  >
                    <Text style={{ fontSize: 9, color: "#0C85A3", fontWeight: 700 }}>+ Add Medication</Text>
                  </div>
                </div>

                {/* Bottom tab bar */}
                <div
                  style={{
                    background: "#fff",
                    borderTop: "1px solid #f0f0f0",
                    display: "flex",
                    justifyContent: "space-around",
                    padding: "5px 0 6px",
                    flexShrink: 0
                  }}
                >
                  {["Meds", "Assistance", "Wallet", "Education", "Help"].map((t, i) => (
                    <div key={t} style={{ textAlign: "center" }}>
                      <div
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: i === 0 ? "#0C85A3" : "#EAE5DD",
                          margin: "0 auto 1px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <span style={{ fontSize: 7, color: i === 0 ? "#fff" : "#8F8C86" }}>●</span>
                      </div>
                      <span
                        style={{ fontSize: 7, color: i === 0 ? "#393735" : "#8F8C86", fontWeight: i === 0 ? 700 : 400 }}
                      >
                        {t}
                      </span>
                      {t === "Help" && (
                        <span
                          style={{
                            position: "absolute",
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            background: "#ff4d4f",
                            marginLeft: -2,
                            marginTop: -14
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ width: 44, height: 4, background: "#c0c0c0", borderRadius: 10, margin: "6px auto 2px" }} />
            </div>
          </div>
        );
      }}
    </Form.Item>
  );

  return (
    <Drawer
      title={isEditing ? `Edit Brand: ${config.brandName}` : "Add New Brand"}
      width={980}
      open={visible}
      onClose={() => onClose(false)}
      destroyOnClose
      closable
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            {isEditing ? "Save Changes" : "Create Brand"}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <div style={{ display: "flex", gap: 32 }}>
          {/* ── Left: Live Preview ──────────────────────────────────────── */}
          <div style={{ width: 340, flexShrink: 0 }}>
            <LivePreview />
          </div>

          {/* ── Right: Form Fields ─────────────────────────────────────── */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Invite Link (existing brands) */}
            {isEditing && config?.inviteSlug && (
              <div
                style={{
                  marginBottom: 16,
                  padding: 12,
                  background: "#f6ffed",
                  borderRadius: 8,
                  border: "1px solid #b7eb8f"
                }}
              >
                <Space>
                  <LinkOutlined style={{ color: "#52c41a" }} />
                  <Text strong style={{ fontSize: 13 }}>
                    Invite Link
                  </Text>
                </Space>
                <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
                  <Input
                    readOnly
                    value={`${window.location.origin}/consumer/invite/${config.inviteSlug}`}
                    style={{ fontSize: 12 }}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/consumer/invite/${config.inviteSlug}`);
                      message.success("Invite link copied!");
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}

            {/* Drug Search + Brand Name */}
            <Form.Item
              label="Search Drug"
              extra="Search for the medication. Selecting a drug auto-fills the brand name."
            >
              <AutoComplete
                options={drugSearchResults}
                onSearch={handleDrugSearch}
                onSelect={handleDrugSelect}
                placeholder="Search by drug name (e.g., Xolair, Ocrevus)"
                defaultValue={config?.rxnormDrugName || undefined}
                allowClear
              />
            </Form.Item>
            <Form.Item name="rxCui" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="rxnormDrugName" hidden>
              <Input />
            </Form.Item>
            <Form.Item
              name="brandName"
              label="Brand Display Name"
              rules={[{ required: true, message: "Please enter the brand name" }]}
            >
              <Input placeholder="e.g., XOLAIR, OCREVUS" />
            </Form.Item>

            {/* Brand Assets */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Form.Item name="brandLogoUrl" label="Brand Logo">
                <ImageUploadField
                  placeholder="Logo URL or upload"
                  accept="image/jpeg,image/png,image/svg+xml,image/webp"
                  onPreviewChange={setLogoPreviewUrl}
                />
              </Form.Item>
              <Form.Item name="bannerImageUrl" label="Banner Image">
                <ImageUploadField
                  placeholder="Banner URL or upload"
                  accept="image/jpeg,image/png,image/webp"
                  onPreviewChange={setBannerPreviewUrl}
                />
              </Form.Item>
            </div>

            <Divider />

            {/* ── Support Channels ── */}
            <Text strong style={{ display: "block", marginBottom: 12, fontSize: 15 }}>
              🎧 Support Channels
            </Text>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <Form.Item
                name="manufacturerHotlineNumber"
                label="Manufacturer Number"
                getValueFromEvent={(e) => formatPhoneNumber(e.target.value)}
                rules={[{ pattern: /^\(\d{3}\) \d{3}-\d{4}$/, message: "Invalid phone" }]}
                style={{ marginBottom: 0 }}
              >
                <Input type="tel" inputMode="numeric" placeholder="(555) 000-0000" addonBefore="📞 +1" />
              </Form.Item>
              <Form.Item
                name="aiSupportPhoneNumber"
                label="AI Support Line"
                getValueFromEvent={(e) => formatPhoneNumber(e.target.value)}
                rules={[{ pattern: /^\(\d{3}\) \d{3}-\d{4}$/, message: "Invalid phone" }]}
                style={{ marginBottom: 0 }}
              >
                <Input type="tel" inputMode="numeric" placeholder="(555) 111-2222" addonBefore="🤖 +1" />
              </Form.Item>
            </div>

            <Divider />

            {/* ── Journey Configuration ── */}
            <Text strong style={{ display: "block", marginBottom: 16, fontSize: 15 }}>
              ⚙️ Journey Configuration
            </Text>

            {Object.entries(STEP_LABELS).map(([stepKey, label]) => (
              <div
                key={stepKey}
                style={{
                  marginBottom: 12,
                  padding: "12px 16px",
                  background: "#fafafa",
                  borderRadius: 10,
                  border: "1px solid #f0f0f0"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: journeySteps[stepKey]?.enabled ? 10 : 0
                  }}
                >
                  <Text strong style={{ fontSize: 14 }}>
                    <span style={{ marginRight: 6 }}>
                      {stepKey === "prescription"
                        ? "💊"
                        : stepKey === "coverage"
                        ? "🛡️"
                        : stepKey === "assistance"
                        ? "💰"
                        : "❤️"}
                    </span>
                    {label}
                  </Text>
                  <Switch
                    checked={journeySteps[stepKey]?.enabled}
                    onChange={(checked) => handleStepToggle(stepKey, checked)}
                  />
                </div>
                {journeySteps[stepKey]?.enabled && journeySteps[stepKey]?.subSteps && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {Object.entries(journeySteps[stepKey].subSteps).map(([subKey, value]) => (
                      <Checkbox
                        key={subKey}
                        checked={typeof value === "object" ? value.enabled !== false : !!value}
                        onChange={(e) => handleSubStepToggle(stepKey, subKey, e.target.checked)}
                        style={{ fontSize: 13 }}
                      >
                        {SUB_STEP_LABELS[subKey] || subKey}
                      </Checkbox>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Support Resources */}
            {renderSupportResources()}
          </div>
        </div>
      </Form>
    </Drawer>
  );
};

export { JourneyConfigurationForm };
